import { ColumnDef } from '@tanstack/react-table'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import dayjs from 'dayjs'

import SortableColumnTitle from '@/components/SortableColumnTitle/SortableColumnTitle'
import { OrganisationInterface } from '@/types'
import type { SortOrder } from '@/utils/table/columnSort'

import cn from './ClientsTab.module.scss'

export type ClientsSortDir = 'asc' | 'desc'

export const getNextClientsSortDir = (
  sortDir: ClientsSortDir | null
): ClientsSortDir | null =>
  sortDir === null ? 'asc' : sortDir === 'asc' ? 'desc' : null

const SORT_ORDERS: Record<ClientsSortDir, SortOrder> = {
  asc: 'ASC',
  desc: 'DESC',
}

export const CLIENTS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  code: '120px',
  description: 'minmax(200px, 1fr)',
  vatNumber: '130px',
  email: 'minmax(160px, 220px)',
  phoneNumber: '160px',
  registrationDate: '190px',
  isLegal: '110px',
}

type ClientsColumnsParams = {
  onNameClick: (client: OrganisationInterface) => void
  sortDir: ClientsSortDir | null
  toggleSort: () => void
  isOrganisationSelected: boolean
}

export const getColumns = ({
  onNameClick,
  sortDir,
  toggleSort,
  isOrganisationSelected,
}: ClientsColumnsParams): ColumnDef<OrganisationInterface, unknown>[] => [
  {
    id: 'code',
    header: 'Код',
    accessorKey: 'code',
    cell: (info) => <span>{info.row.original.code}</span>,
  },
  {
    id: 'description',
    header: 'Наименование',
    accessorKey: 'description',
    cell: (info) => {
      const val = info.row.original.description
      return (
        <Tooltip title={val} mouseEnterDelay={0.4}>
          <button
            type="button"
            className={classNames(
              cn.clientLink,
              isOrganisationSelected && cn.clientLinkSelected
            )}
            onClick={() => onNameClick(info.row.original)}
          >
            {val}
          </button>
        </Tooltip>
      )
    },
  },
  {
    id: 'vatNumber',
    header: 'УНП',
    accessorKey: 'vatNumber',
    cell: (info) => <span>{info.row.original.vatNumber || '—'}</span>,
  },
  {
    id: 'email',
    header: 'Email',
    accessorKey: 'email',
    cell: (info) => {
      const val = info.row.original.email
      if (!val) return <span>—</span>
      return (
        <Tooltip title={val} mouseEnterDelay={0.4}>
          <span className={cn.helpText}>{val}</span>
        </Tooltip>
      )
    },
  },
  {
    id: 'phoneNumber',
    header: 'Телефон',
    accessorKey: 'phoneNumber',
    cell: (info) => {
      const val = info.row.original.phoneNumber
      if (!val) return <span>—</span>

      return (
        <Tooltip title={val} mouseEnterDelay={0.3}>
          <span className={cn.helpText}>{val}</span>
        </Tooltip>
      )
    },
  },
  {
    id: 'registrationDate',
    header: () => (
      <SortableColumnTitle
        title="Дата регистрации"
        order={sortDir ? SORT_ORDERS[sortDir] : null}
        onToggle={toggleSort}
        isOrganisationSelected={isOrganisationSelected}
      />
    ),
    accessorKey: 'registrationDate',
    cell: (info) => {
      const val = info.row.original.registrationDate
      if (!val) return <span>—</span>
      const date = dayjs(val)
      return (
        <Tooltip title={date.format('DD.MM.YYYY HH:mm:ss')}>
          <span>{date.format('DD.MM.YYYY')}</span>
        </Tooltip>
      )
    },
  },
  {
    id: 'isLegal',
    header: 'Статус',
    accessorKey: 'isLegal',
    cell: (info) => {
      const isLegal = info.row.original.isLegal
      return (
        <span className={isLegal ? cn.statusRegistered : cn.statusPending}>
          {isLegal ? 'Активен' : 'Ожидает'}
        </span>
      )
    },
  },
]
